import { cn } from "@/lib/utils";
import type { ChatThemeId } from "@/hooks/voice/useChatTheme";

const HG_BRAND_PRIMARY = "#4E9CFF";
const HG_BRAND_SECONDARY = "#FF4DB8";

interface ChatThemePickerProps {
  themeId: ChatThemeId;
  onChange: (themeId: ChatThemeId) => void;
  /** Class applied to the "Chat style" heading — pass the caller's own
   *  section-label style so it matches its neighbouring "Chat text size"
   *  heading exactly. */
  labelClassName?: string;
}

/**
 * The "Chat style" swatch picker (Imprint / Whisper / Edge) — shared by
 * GenieTextChatOverlayWeb's settings panel and GenieConversationCanvas's
 * gear-icon menu so both surfaces choose from, and preview, the same theme.
 */
export const ChatThemePicker = ({ themeId, onChange, labelClassName = "text-xs font-medium text-slate-600" }: ChatThemePickerProps) => (
  <div>
    <p className={labelClassName}>Chat style</p>
    <p className="mt-0.5 text-[10px] text-slate-400">Pick a look for the conversation.</p>
    <div className="mt-2 grid grid-cols-3 gap-2">
      <button
        type="button"
        onClick={() => onChange("imprint")}
        className={cn(
          "rounded-xl p-2 transition-colors text-left",
          themeId === "imprint" ? "border-2 border-[#4E9CFF] bg-[#4E9CFF14]" : "border border-slate-100 hover:bg-slate-50",
        )}
        title="No bubble. Text sits on the page."
      >
        <div className="h-10 rounded-md bg-white flex flex-col justify-center px-1.5 gap-0.5">
          <span className="text-[7px] font-semibold uppercase tracking-wider" style={{ color: HG_BRAND_SECONDARY }}>
            Genie
          </span>
          <span className="block h-[2px] w-3/4 rounded-sm bg-slate-300" />
          <span className="block h-[2px] w-1/2 rounded-sm bg-slate-300" />
        </div>
        <p className="mt-1.5 text-[10px] font-semibold text-slate-700">Imprint</p>
      </button>
      <button
        type="button"
        onClick={() => onChange("whisper")}
        className={cn(
          "rounded-xl p-2 transition-colors text-left",
          themeId === "whisper" ? "border-2 border-[#4E9CFF] bg-[#4E9CFF14]" : "border border-slate-100 hover:bg-slate-50",
        )}
        title="Barely-there brand tint. No border, no shadow."
      >
        <div className="h-10 rounded-md bg-white flex flex-col justify-center px-1.5 gap-0.5">
          <div className="self-start rounded-lg bg-[#F4F6FB] px-1.5 py-0.5">
            <span className="block h-[2px] w-6 rounded-sm bg-slate-300" />
          </div>
          <div className="self-end rounded-lg bg-[#4E9CFF14] px-1.5 py-0.5">
            <span className="block h-[2px] w-5 rounded-sm bg-slate-300" />
          </div>
        </div>
        <p className="mt-1.5 text-[10px] font-semibold text-slate-700">Whisper</p>
      </button>
      <button
        type="button"
        onClick={() => onChange("edge")}
        className={cn(
          "rounded-xl p-2 transition-colors text-left",
          themeId === "edge" ? "border-2 border-[#4E9CFF] bg-[#4E9CFF14]" : "border border-slate-100 hover:bg-slate-50",
        )}
        title="No bg. Thin brand-color rule on the gutter side."
      >
        <div className="h-10 rounded-md bg-white flex flex-col justify-center px-1.5 gap-1">
          <div className="self-start border-l-2 pl-1" style={{ borderColor: HG_BRAND_SECONDARY }}>
            <span className="block h-[2px] w-6 rounded-sm bg-slate-300" />
          </div>
          <div className="self-end border-r-2 pr-1" style={{ borderColor: HG_BRAND_PRIMARY }}>
            <span className="block h-[2px] w-5 rounded-sm bg-slate-300" />
          </div>
        </div>
        <p className="mt-1.5 text-[10px] font-semibold text-slate-700">Edge</p>
      </button>
    </div>
  </div>
);

export default ChatThemePicker;
